import { useMemo, useRef, useState } from 'react'
import {
  DEFAULT_GAP, DEFAULT_MAX_ITEMS, DEFAULT_CHAR_BUDGET,
  packRows, renderRow, rowLength, separatorLength, truncateToBudget,
} from './tickerFormat'

// Shared tail end of every ticker feed: the template editor, the width controls,
// the packed output and the per-row preview. The feed tabs differ only in where
// their rows come from and how one row becomes a line, so that is all they pass.
//
// Each feed keeps its own settings under its own storage key — a standings line
// and a game recap want different budgets and templates.

const defaults = (presets) => ({
  template: presets[0].template,
  budget: DEFAULT_CHAR_BUDGET,
  maxItems: DEFAULT_MAX_ITEMS,
  gap: DEFAULT_GAP,
  trailing: false,
  mode: 'plain',
  trim: false,
})

export default function TickerRows({
  storageKey, presets, tokens, rows, renderItem, rowLabel, emptyMessage,
}) {
  const [cfg, setCfg] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? { ...defaults(presets), ...JSON.parse(raw) } : defaults(presets)
    } catch { return defaults(presets) }
  })
  const [excluded, setExcluded] = useState(() => new Set())
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const templateRef = useRef(null)

  const set = (patch) => setCfg(prev => {
    const next = { ...prev, ...patch }
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* private mode */ }
    return next
  })

  const rendered = useMemo(() => (rows || []).map((row, i) => {
    const text = renderItem(row, cfg.template)
    return { row, key: rowLabel(row) || String(i), text: cfg.trim ? truncateToBudget(text, cfg.budget) : text }
  }), [rows, cfg.template, cfg.trim, cfg.budget, renderItem, rowLabel])

  const packed = useMemo(() => {
    const items = rendered.filter(r => !excluded.has(r.key)).map(r => r.text)
    return packRows(items, cfg).map(r => ({ ...r, rendered: renderRow(r, cfg) }))
  }, [rendered, excluded, cfg])

  const output = packed.map(r => r.rendered).join('\n')
  const overCount = packed.filter(r => r.over).length

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true); setTimeout(() => setCopied(false), 1800)
    } catch { setError('Clipboard blocked — select the text and copy manually.') }
  }

  const insertToken = (key) => {
    const el = templateRef.current
    const token = `<${key}>`
    if (!el) { set({ template: `${cfg.template}${token}` }); return }
    const { selectionStart: a, selectionEnd: b } = el
    set({ template: cfg.template.slice(0, a) + token + cfg.template.slice(b) })
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length) })
  }

  const toggle = (key) => setExcluded(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  return (
    <div>
      {error && (
        <div className="card" style={{ padding: 10, marginBottom: 12, borderLeft: '3px solid #e2574a' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>Line text</strong>
          {presets.map(p => (
            <button key={p.id} onClick={() => set({ template: p.template })}
              style={{
                fontSize: 12, padding: '3px 9px', borderRadius: 12,
                border: '1px solid var(--border-color, #2a2a2a)',
                background: cfg.template === p.template ? 'var(--accent, #4a90e2)' : 'transparent',
                color: cfg.template === p.template ? '#fff' : 'var(--text-muted, #888)',
                cursor: 'pointer',
              }}>{p.label}</button>
          ))}
        </div>

        <textarea ref={templateRef} value={cfg.template} rows={2}
          onChange={e => set({ template: e.target.value })}
          style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 }} />

        <p className="muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
          Click a token to insert it. Wrap a phrase in <code>[square brackets]</code> to drop it when a
          token inside has no value. <code>&lt;gap8&gt;</code> inserts 8 non-breaking spaces.
        </p>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {tokens.map(t => (
            <button key={t.key} onClick={() => insertToken(t.key)} title={t.hint}
              style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 10,
                border: '1px solid var(--border-color, #2a2a2a)', background: 'transparent',
                color: 'var(--text-muted, #888)', cursor: 'pointer',
                fontFamily: 'ui-monospace, Menlo, monospace',
              }}>{`<${t.key}>`}</button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex',
        gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Characters per line</span>
          <input type="number" min="20" max="600" value={cfg.budget} style={{ width: 90 }}
            onChange={e => set({ budget: Number(e.target.value) || DEFAULT_CHAR_BUDGET })} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Max items per line</span>
          <input type="number" min="1" max="12" value={cfg.maxItems} style={{ width: 70 }}
            onChange={e => set({ maxItems: Math.max(1, Number(e.target.value) || 1) })} />
        </label>
        {cfg.maxItems > 1 && (
          <>
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>Gap (nbsp each side)</span>
              <input type="number" min="0" max="40" value={cfg.gap} style={{ width: 70 }}
                onChange={e => set({ gap: Math.max(0, Number(e.target.value) || 0) })} />
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <input type="checkbox" checked={cfg.trailing}
                onChange={e => set({ trailing: e.target.checked })} />
              Trailing separator
            </label>
          </>
        )}
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Output</span>
          <select value={cfg.mode} onChange={e => set({ mode: e.target.value })}>
            <option value="plain">Plain text</option>
            <option value="formula">Sheets formula</option>
          </select>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input type="checkbox" checked={cfg.trim} onChange={e => set({ trim: e.target.checked })} />
          Trim over-long lines
        </label>
        {cfg.maxItems > 1 && (
          <div className="muted" style={{ fontSize: 11, maxWidth: 380, lineHeight: 1.45 }}>
            Each join costs {separatorLength(cfg.gap)} characters. Use the Sheets formula output —
            plain nbsp can be normalized away on paste.
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 13 }}>{packed.length} ticker row{packed.length === 1 ? '' : 's'}</strong>
          {overCount > 0 && (
            <span style={{ fontSize: 12, color: '#e2a24a' }}>{overCount} over {cfg.budget} characters</span>
          )}
          <span style={{ flex: 1 }} />
          <button onClick={copy} disabled={!packed.length}>{copied ? 'Copied' : 'Copy all rows'}</button>
          <button onClick={() => {
            const blob = new Blob([output], { type: 'text/plain;charset=utf-8' })
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = `ticker-${storageKey.split('.').slice(-2, -1)}-${new Date().toISOString().slice(0, 10)}.txt`
            a.click(); URL.revokeObjectURL(a.href)
          }} disabled={!packed.length}>Download .txt</button>
        </div>
        <textarea readOnly value={output} rows={Math.min(12, Math.max(4, packed.length + 1))}
          style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} />
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color, #2a2a2a)' }}>
              <th style={{ padding: '8px 10px', width: 34 }}></th>
              <th style={{ padding: '8px 10px', width: 110 }}>Group</th>
              <th style={{ padding: '8px 10px' }}>Ticker line</th>
              <th style={{ padding: '8px 10px', width: 56, textAlign: 'right' }}>Chars</th>
            </tr>
          </thead>
          <tbody>
            {rendered.map(r => {
              const off = excluded.has(r.key)
              const len = rowLength([r.text], { gap: cfg.gap })
              return (
                <tr key={r.key} style={{
                  borderBottom: '1px solid var(--border-color, #1f1f1f)', opacity: off ? 0.4 : 1,
                }}>
                  <td style={{ padding: '6px 10px' }}>
                    <input type="checkbox" checked={!off} onChange={() => toggle(r.key)} />
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">{r.key}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'ui-monospace, Menlo, monospace' }}>{r.text}</td>
                  <td style={{
                    padding: '6px 10px', textAlign: 'right',
                    color: len > cfg.budget ? '#e2a24a' : 'var(--text-muted, #888)',
                  }}>{len}</td>
                </tr>
              )
            })}
            {rendered.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 16 }} className="muted">{emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
